import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  Rating,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdStar,
  MdRateReview,
  MdThumbUp,
  MdThumbDown,
  MdWarningAmber,
  MdInventory2,
  MdPercent,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/customer-ratings';
const STAR_COLORS = ['#16a34a', '#65a30d', '#eab308', '#f97316', '#dc2626']; // 5★ → 1★
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 12 });
const ratingColor = (v) => (v >= 4 ? '#15803d' : v >= 3 ? '#b45309' : '#b91c1c');

const Stars = ({ value, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Rating value={value} precision={0.1} readOnly size="small" />
    <Typography sx={{ fontSize: 12.5, fontWeight: 800, fontFamily: FONT, color: ratingColor(value) }}>{value}</Typography>
    {count !== undefined && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>({count})</Typography>}
  </Box>
);

const DistBar = ({ dist, count }) =>
  !Array.isArray(dist) || !count ? (
    <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>
  ) : (
  <Tooltip title={dist.map((n, i) => `${5 - i}★: ${n}`).join('  ·  ')} arrow>
    <Box sx={{ display: 'flex', width: 140, height: 10, borderRadius: 5, overflow: 'hidden', background: '#e2e8f0' }}>
      {dist.map((n, i) => (n ? <Box key={i} sx={{ width: `${(n / count) * 100}%`, background: STAR_COLORS[i] }} /> : null))}
    </Box>
  </Tooltip>
);

const ProductCell = ({ r }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
    <Avatar variant="rounded" src={r.image || undefined} alt={r.product} sx={{ width: 36, height: 36, bgcolor: '#e2e8f0', color: '#475569', fontWeight: 700, fontSize: 14 }}>
      {(r.product || '?').charAt(0).toUpperCase()}
    </Avatar>
    <Box sx={{ lineHeight: 1.25, minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: FONT, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.product}</Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{[r.brand, r.productCode].filter(Boolean).join(' · ') || r.category || '—'}</Typography>
    </Box>
  </Box>
);

const ReviewText = ({ text }) =>
  text ? (
    <Tooltip title={text} arrow placement="top-start">
      <Typography sx={{ fontSize: 12.5, fontFamily: FONT, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155' }}>“{text}”</Typography>
    </Tooltip>
  ) : (
    <Typography sx={{ fontSize: 12, color: '#94a3b8', fontFamily: FONT }}>No written review</Typography>
  );

const col = (key, label, opts = {}) => ({ key, label, ...opts });
const groupCols = [
  col('avgRating', 'Avg Rating', { text: (r) => r.avgRating, cell: (r) => <Stars value={r.avgRating} /> }),
  col('count', 'Ratings', { numeric: true, bold: true, text: (r) => r.count }),
  col('dist', 'Distribution (5★→1★)', { text: (r) => r.dist.join(' / '), cell: (r) => <DistBar dist={r.dist} count={r.count} /> }),
  col('positivePct', 'Positive', { numeric: true, text: (r) => `${r.positivePct}%`, cell: (r) => <Chip size="small" label={`${r.positivePct}%`} sx={pill('#dcfce7', '#166534')} /> }),
  col('negativePct', 'Negative', { numeric: true, text: (r) => `${r.negativePct}%`, cell: (r) => <Chip size="small" label={`${r.negativePct}%`} sx={r.negativePct ? pill('#fee2e2', '#b91c1c') : pill('#f1f5f9', '#64748b')} /> }),
  col('withText', 'Written', { numeric: true, text: (r) => r.withText }),
];
const productGroupCols = [
  col('product', 'Product', { text: (r) => r.product, cell: (r) => <ProductCell r={r} />, extra: (r) => ({ Code: r.productCode, Brand: r.brand, Category: r.category }) }),
  ...groupCols,
  col('lastReview', 'Latest Written Review', { text: (r) => r.lastReview, cell: (r) => <ReviewText text={r.lastReview} /> }),
  col('lastAt', 'Last Rated', { text: (r) => fmtDate(r.lastAt) }),
];
const GROUP_SORTS = [
  ['count_desc', 'Most ratings'],
  ['rating_asc', 'Lowest rated first'],
  ['rating_desc', 'Highest rated first'],
  ['negative_desc', 'Most negative %'],
];

const TABS = [
  {
    key: 'reviews',
    label: 'All Reviews',
    sorts: [
      ['date_desc', 'Newest first'],
      ['date_asc', 'Oldest first'],
      ['rating_asc', 'Lowest rating first'],
      ['rating_desc', 'Highest rating first'],
    ],
    columns: [
      col('date', 'Date', { text: (r) => fmtDate(r.date) }),
      col('rating', 'Rating', {
        text: (r) => r.rating,
        cell: (r) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Rating value={r.rating} readOnly size="small" />
          </Box>
        ),
      }),
      col('review', 'Review', { text: (r) => r.review, cell: (r) => <ReviewText text={r.review} /> }),
      col('product', 'Product', { text: (r) => r.product, cell: (r) => <ProductCell r={r} />, extra: (r) => ({ Code: r.productCode, Brand: r.brand, Category: r.category }) }),
      col('customer', 'Customer', {
        text: (r) => r.customer,
        extra: (r) => ({ Phone: r.phone }),
        cell: (r) => (
          <Box sx={{ lineHeight: 1.25 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: FONT }}>{r.customer}</Typography>
            {r.phone && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{r.phone}</Typography>}
          </Box>
        ),
      }),
      col('orderNumber', 'Order No.', { text: (r) => r.orderNumber }),
    ],
  },
  { key: 'products', label: 'By Product', sorts: GROUP_SORTS, columns: productGroupCols },
  {
    key: 'categories',
    label: 'By Category',
    sorts: GROUP_SORTS,
    columns: [col('category', 'Category', { bold: true, text: (r) => r.category }), col('productsRated', 'Products Rated', { numeric: true, text: (r) => r.productsRated }), ...groupCols],
  },
  { key: 'attention', label: 'Needs Attention', sorts: GROUP_SORTS, columns: productGroupCols },
];

const RatingsReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stars, setStars] = useState('');
  const [sentiment, setSentiment] = useState('');
  const [withText, setWithText] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [minRatings, setMinRatings] = useState('3');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { tab: tab.key, sortBy, minRatings };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (category) p.category = category;
    if (brand) p.brand = brand;
    if (tab.key === 'reviews') {
      if (stars) p.stars = stars;
      if (sentiment) p.sentiment = sentiment;
      if (withText) p.withText = withText;
    }
    return p;
  }, [tab.key, sortBy, minRatings, from, to, search, category, brand, stars, sentiment, withText]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (i) => {
    setTabIndex(i);
    setSortBy(TABS[i].key === 'reviews' ? 'date_desc' : TABS[i].key === 'attention' ? 'rating_asc' : 'count_desc');
    setPage(0);
  };
  const reviewsWith = (setter, value) => {
    goToTab(0);
    setStars('');
    setSentiment('');
    setWithText('');
    setter(value);
  };

  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };
  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };
  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };
  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setStars('');
    setSentiment('');
    setWithText('');
    setCategory('');
    setBrand('');
    setMinRatings('3');
    setSortBy(tab.key === 'reviews' ? 'date_desc' : tab.key === 'attention' ? 'rating_asc' : 'count_desc');
    setPage(0);
  };

  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of tab.columns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `Ratings_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Customer Ratings — ${tab.label}`,
          subtitle: `${rangeLabel(from, to)} · ${rows.length} rows`,
          headers: tab.columns.map((c) => c.label),
          cells: rows.map((r) => tab.columns.map((c) => ({ value: c.key === 'rating' ? '★'.repeat(r.rating) : c.text(r), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  // Rows from the previous tab stay in state until the new fetch returns; don't render them
  // with this tab's columns.
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const filterOptions = data?.filterOptions || { categories: [], brands: [] };
  const dist = stats.distribution || [0, 0, 0, 0, 0];
  const distMax = Math.max(1, ...dist);

  const cards = [
    { label: 'Average Rating', value: stats.avgRating ? `${stats.avgRating} ★` : '—', icon: MdStar, gradient: 'linear-gradient(135deg,#eab308,#ca8a04)', sub: `${stats.ratings || 0} ratings` },
    { label: 'Written Reviews', value: (stats.withText || 0).toLocaleString('en-IN'), icon: MdRateReview, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: 'Click to read them', onClick: () => reviewsWith(setWithText, 'yes'), active: tab.key === 'reviews' && withText === 'yes' },
    { label: 'Positive (4–5★)', value: `${stats.positivePct || 0}%`, icon: MdThumbUp, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: 'Click to filter', onClick: () => reviewsWith(setSentiment, 'positive'), active: tab.key === 'reviews' && sentiment === 'positive' },
    { label: 'Negative (1–2★)', value: `${stats.negativePct || 0}%`, icon: MdThumbDown, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: 'Click to filter', onClick: () => reviewsWith(setSentiment, 'negative'), active: tab.key === 'reviews' && sentiment === 'negative' },
    { label: 'Needs Attention', value: (stats.attention || 0).toLocaleString('en-IN'), icon: MdWarningAmber, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `Products ≤ 3★ with ${minRatings}+ ratings`, onClick: () => goToTab(3), active: tab.key === 'attention' },
    { label: 'Products Rated', value: (stats.productsRated || 0).toLocaleString('en-IN'), icon: MdInventory2, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'With at least one rating' },
    { label: 'Review Coverage', value: stats.coveragePct === null || stats.coveragePct === undefined ? '—' : `${stats.coveragePct}%`, icon: MdPercent, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: `Of ${stats.deliveredLines || 0} delivered items` },
  ];

  const activeFilterCount = [from || to, search, stars, sentiment, withText, category, brand].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Customer Ratings Report</Typography>
          <Typography sx={{ fontSize: 14, color: '#64748b', fontFamily: FONT }}>What customers say about your products</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Rating distribution */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 44, fontWeight: 800, fontFamily: FONT, color: ratingColor(stats.avgRating || 0), lineHeight: 1 }}>{stats.avgRating || '—'}</Typography>
            <Rating value={stats.avgRating || 0} precision={0.1} readOnly />
            <Typography sx={{ fontSize: 13, color: '#64748b', fontFamily: FONT }}>{stats.ratings || 0} ratings</Typography>
          </Grid>
          <Grid item xs={12} md={9}>
            {dist.map((n, i) => {
              const star = 5 - i;
              const selected = tab.key === 'reviews' && String(stars) === String(star);
              return (
                <Box
                  key={star}
                  onClick={() => reviewsWith(setStars, selected ? '' : String(star))}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, px: 1, borderRadius: '8px', cursor: 'pointer', background: selected ? '#fef9c3' : 'transparent', '&:hover': { background: '#f8fafc' } }}
                >
                  <Typography sx={{ width: 30, fontWeight: 700, fontFamily: FONT, fontSize: 13 }}>{star}★</Typography>
                  <LinearProgress variant="determinate" value={(n / distMax) * 100} sx={{ flex: 1, height: 12, borderRadius: 6, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: STAR_COLORS[i], borderRadius: 6 } }} />
                  <Typography sx={{ width: 90, textAlign: 'right', fontSize: 13, fontWeight: 700, fontFamily: FONT }}>
                    {n} <Typography component="span" sx={{ color: '#64748b', fontSize: 12 }}>({stats.ratings ? Math.round((n / stats.ratings) * 100) : 0}%)</Typography>
                  </Typography>
                </Box>
              );
            })}
          </Grid>
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <TextField
              label="Search product / customer / order / review text"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Category" size="small" fullWidth value={category} onChange={setFilter(setCategory)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All categories</MenuItem>
              {filterOptions.categories.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Brand" size="small" fullWidth value={brand} onChange={setFilter(setBrand)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All brands</MenuItem>
              {filterOptions.brands.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {tab.key === 'reviews' && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField select label="Stars" size="small" fullWidth value={stars} onChange={setFilter(setStars)}>
                  <MenuItem value="">All</MenuItem>
                  {[5, 4, 3, 2, 1].map((s) => (
                    <MenuItem key={s} value={String(s)}>{'★'.repeat(s)} ({s})</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField select label="Sentiment" size="small" fullWidth value={sentiment} onChange={setFilter(setSentiment)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="positive">Positive (4–5★)</MenuItem>
                  <MenuItem value="neutral">Neutral (3★)</MenuItem>
                  <MenuItem value="negative">Negative (1–2★)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField select label="Written review" size="small" fullWidth value={withText} onChange={setFilter(setWithText)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="yes">With text</MenuItem>
                  <MenuItem value="no">Stars only</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
          {tab.key === 'attention' && (
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <TextField select label="Minimum ratings" size="small" fullWidth value={minRatings} onChange={setFilter(setMinRatings)}>
                {['1', '2', '3', '5', '10'].map((n) => (
                  <MenuItem key={n} value={n}>{n}+ ratings</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {tab.sorts.map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} rows</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell key={c.key} align={c.numeric ? 'right' : 'left'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>
                    {tab.key === 'attention' ? 'No products need attention — nice work.' : 'Nothing matches the selected filters.'}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{ '&:nth-of-type(even)': { background: '#f8fafc' }, boxShadow: tab.key === 'reviews' && r.rating <= 2 ? 'inset 4px 0 0 #dc2626' : 'none' }}
                >
                  {tab.columns.map((c) => (
                    <TableCell key={c.key} align={c.numeric ? 'right' : 'left'} sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 13, fontWeight: c.bold ? 700 : 500, color: '#334155' }}>
                      {c.cell ? c.cell(r) : c.text(r)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RatingsReport;
